import React, { useEffect, useMemo, useRef, useState } from "react";
import FormInput from "../formInput/formInput";
import TextInput from "../textInput/textInput";
import SelectInput from "../selectInput/select";
import MedicalPortalAccess from "../selectInput/portal";
import DynamicCheckbox from "../selectInput/consent";
import Signature from "../signature/signature";
import FamilyMemberConsent from "../familyMembers/familyMembers";
import DOBPickerInput from "../dobPicker/dobPicker";

const buttonClass =
  "px-4 py-2 bg-sky-600 text-white rounded-xl shadow-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-opacity-50 transition-all text-sm font-semibold";

const tabs = [
  { key: "navigation", label: "Form navigation", icon: "fas fa-route" },
  { key: "required", label: "Required fields", icon: "fas fa-asterisk" },
  { key: "signature", label: "Signature", icon: "fas fa-signature" },
  { key: "contact", label: "Email and Phone numbers", icon: "fas fa-envelope" },
  { key: "zip", label: "ZIP Lookup", icon: "fas fa-map-marker-alt" },
  { key: "family", label: "Family Members", icon: "fas fa-users" },
];

const topicMap = {
  navigation: {
    title: "Form navigation",
    subtitle: "Simple steps to go page by page without confusion.",
    steps: [
      "Use Next to go forward. Use Previous if you need to fix something.",
      "Fill all required fields on the current page before moving ahead. If not a warning popup will appear listing the required fields to fill or fix",
      "At the end, click Update Demographics or Register Patient based on the form shown.",
    ],
    tip: "Use this panel while entering data so you do not have to leave the form.",
  },
  required: {
    title: "Required fields",
    subtitle: "If you see *, that field must be filled.",
    steps: [
      "Common required fields are Name, DOB, ZIP, City/State, and Cell Phone.",
      "Required signature or consent fields must be completed before submit.",
      "If red validation appears, fill those fields first, then click Next again.",
    ],
    tip: "Checking required fields page by page saves time at the end.",
  },
  signature: {
    title: "Signature Help",
    subtitle: "How to save or redo a signature.",
    steps: [
      "Patient/Guardian can sign inside the dotted signature box.",
      "If wrong, click Clear and sign again.",
      "Once done, click on the save button to save the signature. On the rest of the pages simply click on I agree button to replicate the signature across all the forms that require it.",
    ],
    tip: "A saved signature is needed to complete final submission.",
  },
  contact: {
    title: "Email and Phone numbers",
    subtitle: "Enter phone and email in valid format.",
    steps: [
      "Phone fields should be valid numbers (Cell/Home/Work/Emergency).",
      "Email should look like name@example.com.",
      "If Portal Access is Yes, a valid email is required.",
    ],
    tip: "Correct contact details help reminders and portal setup.",
  },
  zip: {
    title: "ZIP Lookup",
    subtitle: "ZIP can auto-fill City and State.",
    steps: [
      "Enter ZIP and use search/lookup.",
      "City and State fill automatically from ZIP.",
      "If not filled, recheck ZIP and try again.",
    ],
    tip: "Use ZIP first to avoid address mismatch.",
  },
  family: {
    title: "Family Members",
    subtitle: "How to fill family member consent rows.",
    steps: [
      "Complete one row first, then click Add Family Member.",
      "Fill Name, Relationship, DOB (MM/DD/YYYY), and Phone.",
      "Use the delete icon to remove extra rows. The first row resets instead of full remove.",
    ],
    tip: "Add only people approved to receive care updates.",
  },
};

function HelpForm({ onClose }) {
  const [activeTab, setActiveTab] = useState("navigation");
  const [formPage, setFormPage] = useState(1);
  const [demoFormData, setDemoFormData] = useState({
    firstName: "John",
    lastName: "Carter",
    dob: "08/14/1979",
    gender: "Male",
    zip: "33609",
    city: "Tampa",
    state: "FL",
    cellphone: "8135551234",
    email: "john.carter@example.com",
    consentToText: true,
    portal: true,
    patient_signature: "",
    familyMembers: [
      {
        name: "Mary Carter",
        relationship: "Spouse",
        dob: "06/22/1981",
        mobile_number: "8135558899",
      },
    ],
  });
  const activeContent = useMemo(() => topicMap[activeTab], [activeTab]);
  const activeTabMeta = useMemo(
    () => tabs.find((tab) => tab.key === activeTab) || tabs[0],
    [activeTab]
  );
  const topicToTourTarget = useMemo(
    () => ({
      navigation: { page: 1, section: "nav" },
      required: { page: 1, section: "required" },
      zip: { page: 1, section: "zip" },
      contact: { page: 2, section: "contact" },
      signature: { page: 2, section: "signature" },
      family: { page: 3, section: "family" },
    }),
    []
  );
  const [signatures, setSignatures] = useState({
    patient_signature: "",
  });
  const [isEditing, setIsEditing] = useState({});
  const [invalidFields] = useState([]);
  const canvasRefs = {
    patient_signature: React.useRef(null),
  };

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const onFieldChange = (e) => {
    const key = e.target.name || e.target.id;
    const value = e.target.value;
    setDemoFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleCheckChange = (fieldName, value) => {
    setDemoFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleZipSearch = () => {
    const zipMap = {
      "33609": { city: "Tampa", state: "FL" },
      "33606": { city: "Tampa", state: "FL" },
      "33511": { city: "Brandon", state: "FL" },
    };
    const result = zipMap[(demoFormData.zip || "").trim()];
    setDemoFormData((prev) => ({
      ...prev,
      city: result?.city || "",
      state: result?.state || "",
    }));
  };

  const updateSignatureData = (type) => {
    const canvas = canvasRefs[type]?.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL(0.3);
    setSignatures((prev) => ({ ...prev, [type]: dataUrl }));
    setDemoFormData((prev) => ({ ...prev, [type]: dataUrl }));
  };

  const clearSignature = (type) => {
    const canvas = canvasRefs[type]?.current;
    if (canvas) canvas.clear();
    setSignatures((prev) => ({ ...prev, [type]: "" }));
    setDemoFormData((prev) => ({ ...prev, [type]: "" }));
    setIsEditing((prev) => ({ ...prev, [type]: false }));
  };

  const toggleEditMode = (type, editMode) => {
    setIsEditing((prev) => ({ ...prev, [type]: editMode }));
  };

  const handleFamilyChange = (index, field, value) => {
    setDemoFormData((prev) => {
      const updated = [...prev.familyMembers];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, familyMembers: updated };
    });
  };

  const handleAddMember = () => {
    setDemoFormData((prev) => ({
      ...prev,
      familyMembers: [
        ...prev.familyMembers,
        { name: "", relationship: "", dob: "", mobile_number: "" },
      ],
    }));
  };

  const handleRemoveFamilyMember = (index) => {
    setDemoFormData((prev) => {
      if (index === 0) {
        return {
          ...prev,
          familyMembers: [{ name: "", relationship: "", dob: "", mobile_number: "" }],
        };
      }
      return {
        ...prev,
        familyMembers: prev.familyMembers.filter((_, i) => i !== index),
      };
    });
  };

  const startTopicTour = (key) => {
    const steps = topicTourMap[key] || [];
    if (!steps.length) return;
    setActiveTourSteps(steps);
    setTourStepIndex(0);
    setIsTourActive(true);
    setActiveTab(key);
    setFormPage(steps[0].page);
  };

  const handleTopicClick = (key) => {
    setActiveTab(key);
    const target = topicToTourTarget[key];
    if (target) {
      setFormPage(target.page);
    }
    startTopicTour(key);
  };

  const activeTourSection = topicToTourTarget[activeTab]?.section;
  const modalBodyRef = useRef(null);
  const tooltipRef = useRef(null);
  const tourRefs = useRef({});
  const [isTourActive, setIsTourActive] = useState(false);
  const [activeTourSteps, setActiveTourSteps] = useState([]);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipState, setTooltipState] = useState({ top: 100, left: 100, placement: "bottom" });

  const topicTourMap = useMemo(
    () => ({
      navigation: [
        { page: 1, target: "form-header", topic: "navigation", title: "Form navigation", description: "This header shows current form and page." },
        { page: 1, target: "page-badge", topic: "navigation", title: "Form navigation", description: "Use page count to track your progress." },
        { page: 1, target: "nav-actions", topic: "navigation", title: "Form navigation", description: "Use Previous and Next to move between pages." },
      ],
      required: [
        { page: 1, target: "required-group", topic: "required", title: "Required fields", description: "Fields with * are mandatory in this block." },
        { page: 2, target: "contact-group", topic: "required", title: "Required fields", description: "Required contact values must be present." },
        { page: 2, target: "signature-group", topic: "required", title: "Required fields", description: "Required signature must be completed before submit." },
      ],
      zip: [
        { page: 1, target: "zip-guide", topic: "zip", title: "ZIP Lookup", description: "Use ZIP format shown in this guide." },
        { page: 1, target: "zip-group", topic: "zip", title: "ZIP Lookup", description: "Enter ZIP first in this section." },
        { page: 1, target: "zip-group", topic: "zip", title: "ZIP Lookup", description: "City and State are expected from ZIP lookup." },
      ],
      contact: [
        { page: 2, target: "contact-guide", topic: "contact", title: "Email and Phone numbers", description: "Follow valid phone and email formats here." },
        { page: 2, target: "contact-group", topic: "contact", title: "Email and Phone numbers", description: "Phone and email are entered in this block." },
        { page: 2, target: "portal-group", topic: "contact", title: "Email and Phone numbers", description: "Portal Access may require valid email." },
      ],
      signature: [
        { page: 2, target: "signature-group", topic: "signature", title: "Signature", description: "This is the signature section." },
        { page: 2, target: "signature-group", topic: "signature", title: "Signature", description: "Use clear and re-sign in live workflow." },
        { page: 2, target: "signature-group", topic: "signature", title: "Signature", description: "Required signature must be finished before final submit." },
      ],
      family: [
        { page: 3, target: "family-group", topic: "family", title: "Family Members", description: "Fill one full row first: Name, Relationship, DOB, Phone." },
        { page: 3, target: "family-group", topic: "family", title: "Family Members", description: "Use Add Family Member for additional rows." },
        { page: 3, target: "family-group", topic: "family", title: "Family Members", description: "Use Delete for extra rows; first row resets in app behavior." },
      ],
    }),
    []
  );

  const currentTourStep = isTourActive ? activeTourSteps[tourStepIndex] : null;
  const highlightClass =
    "ring-2 ring-sky-500 ring-offset-1 ring-offset-white bg-sky-50 rounded-xl transition-all duration-200";
  const requiredHighlightClass =
    activeTab === "required" ? "ring-2 ring-sky-400 bg-sky-50/70 rounded-lg p-1" : "";
  const highlightIf = (section) => (activeTourSection === section ? highlightClass : "");
  const setTourRef = (key) => (el) => {
    if (el) tourRefs.current[key] = el;
  };

  const computePlacement = (rect, tooltipWidth, tooltipHeight) => {
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const topSpace = rect.top;
    const bottomSpace = vh - rect.bottom;
    const leftSpace = rect.left;
    const rightSpace = vw - rect.right;
    const placements = [
      { key: "bottom", score: bottomSpace },
      { key: "top", score: topSpace },
      { key: "right", score: rightSpace },
      { key: "left", score: leftSpace },
    ].sort((a, b) => b.score - a.score);

    let placement = placements[0].key;
    let top = rect.bottom + margin;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    if (placement === "top") {
      top = rect.top - tooltipHeight - margin;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (placement === "right") {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + margin;
    } else if (placement === "left") {
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - margin;
    }

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    top = clamp(top, 10, vh - tooltipHeight - 10);
    left = clamp(left, 10, vw - tooltipWidth - 10);
    return { top, left, placement };
  };

  const syncTourPosition = () => {
    if (!isTourActive || !currentTourStep) return;
    const target = tourRefs.current[currentTourStep.target];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

    window.requestAnimationFrame(() => {
      const rect = target.getBoundingClientRect();
      setSpotlightRect(rect);
      const tooltipWidth = Math.min(360, window.innerWidth - 20);
      const tooltipHeight = tooltipRef.current?.offsetHeight || 180;
      setTooltipState(computePlacement(rect, tooltipWidth, tooltipHeight));
    });
  };

  const closeTour = () => {
    setIsTourActive(false);
    setActiveTourSteps([]);
    setSpotlightRect(null);
  };

  const goNextTour = () => {
    if (tourStepIndex >= activeTourSteps.length - 1) {
      closeTour();
      return;
    }
    setTourStepIndex((prev) => prev + 1);
  };

  const goPrevTour = () => {
    setTourStepIndex((prev) => Math.max(0, prev - 1));
  };

  useEffect(() => {
    if (!currentTourStep || !isTourActive) return;
    setActiveTab(currentTourStep.topic);
    if (formPage !== currentTourStep.page) {
      setFormPage(currentTourStep.page);
    }
  }, [currentTourStep, isTourActive, formPage]);

  useEffect(() => {
    if (!isTourActive) return;
    const timer = setTimeout(syncTourPosition, 180);
    const onResize = () => syncTourPosition();
    const onScroll = () => syncTourPosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    modalBodyRef.current?.addEventListener("scroll", onScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      modalBodyRef.current?.removeEventListener("scroll", onScroll);
    };
  }, [isTourActive, tourStepIndex, formPage]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-4">
      <div ref={modalBodyRef} className="w-full max-w-7xl max-h-[92vh] overflow-y-auto bg-gray-100 border border-gray-200 rounded-3xl shadow-2xl">
        <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-sky-700">Form Navigation Help</h2>
              <p className="text-gray-600 text-xs sm:text-sm mt-1">
                Quick guidance for completing forms smoothly.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-1.5 bg-sky-600 text-white rounded-full shadow-md hover:bg-sky-700 transition-all font-semibold text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>

        <div className="px-3 sm:px-4 pb-3">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2 rounded-2xl border border-sky-100 bg-gray-100 p-2.5">
              <div className="flex flex-wrap gap-1.5">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleTopicClick(tab.key)}
                    className={`${buttonClass} flex items-center gap-2 ${
                      activeTab === tab.key ? "ring-2 ring-sky-300 bg-sky-700" : ""
                    }`}
                  >
                    <i className={`${tab.icon}`} aria-hidden="true" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              <div className="mt-2 rounded-2xl border border-sky-100 bg-gray-100 p-2.5">
                <div className="flex items-start gap-2">
                  <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center text-lg">
                    <i className={activeTabMeta.icon} aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-sky-700 leading-tight">{activeContent.title}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mt-1">{activeContent.subtitle}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-1.5 mt-2">
                  {activeContent.steps.map((step, index) => (
                    <div
                      key={`${activeTab}-step-${index}`}
                      className="rounded-xl bg-gray-50 border border-gray-200 p-2 shadow-sm"
                    >
                      <p className="text-sky-600 font-bold mb-1 text-xs">STEP {index + 1}</p>
                      <p className="text-gray-700 leading-relaxed text-xs sm:text-sm">{step}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-1.5 rounded-xl border border-dashed border-sky-300 bg-sky-50 px-2.5 py-1.5 text-sky-800 text-xs sm:text-sm">
                  <span className="font-bold">Quick tip:</span> {activeContent.tip}
                </div>
              </div>
            </div>

            <div className="help-form-demo lg:col-span-3 rounded-2xl border border-sky-100 bg-white p-3">
              <div ref={setTourRef("form-header")} className={`flex items-center justify-between mb-3 ${highlightIf("nav")}`}>
                <h3 className="text-lg font-bold text-sky-700">Patient Form Demo</h3>
                <span ref={setTourRef("page-badge")} className="text-xs font-semibold text-sky-700 bg-sky-100 px-2 py-1 rounded-full">
                  Page {formPage} of 3
                </span>
              </div>

              {formPage === 1 && (
                <div className="space-y-3">
                  <div ref={setTourRef("zip-guide")} className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs sm:text-sm text-sky-800">
                    Format guide: `DOB` as `MM/DD/YYYY`, `ZIP` as 5 digits (example: 33609).
                  </div>
                  <div ref={setTourRef("required-group")} className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${highlightIf("required")}`}>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="firstName"
                        label="First Name"
                        value={demoFormData.firstName}
                        onChange={onFieldChange}
                        placeholder="John"
                        required
                        disabled
                      />
                    </div>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="lastName"
                        label="Last Name"
                        value={demoFormData.lastName}
                        onChange={onFieldChange}
                        placeholder="Smith"
                        required
                        disabled
                      />
                    </div>
                  </div>
                  <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${highlightIf("required")}`}>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="dob"
                        label="DOB"
                        value={demoFormData.dob}
                        onChange={onFieldChange}
                        placeholder="MM/DD/YYYY"
                        required
                        disabled
                      />
                    </div>
                    <div className={requiredHighlightClass}>
                      <SelectInput
                        id="gender"
                        label="Gender"
                        options={genderOptions}
                        value={demoFormData.gender}
                        onChange={onFieldChange}
                        required
                        disabled
                      />
                    </div>
                  </div>
                  <div ref={setTourRef("zip-group")} className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${highlightIf("zip")}`}>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="zip"
                        label="Zip"
                        value={demoFormData.zip}
                        onChange={onFieldChange}
                        placeholder="33609"
                        required
                        onZipSearch={handleZipSearch}
                        disabled
                      />
                    </div>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="city"
                        label="City"
                        value={demoFormData.city}
                        onChange={onFieldChange}
                        disabled
                        required
                      />
                    </div>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="state"
                        label="State"
                        value={demoFormData.state}
                        onChange={onFieldChange}
                        disabled
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {formPage === 2 && (
                <div className="space-y-3">
                  <div ref={setTourRef("contact-guide")} className={`rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs sm:text-sm text-sky-800 ${highlightIf("contact")}`}>
                    Format guide: `Cell Phone` 10 digits (example: 8135551234), `Email` format: name@example.com.
                  </div>
                  <div ref={setTourRef("contact-group")} className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${highlightIf("contact")}`}>
                    <div className={requiredHighlightClass}>
                      <FormInput
                        id="cellphone"
                        label="Cell Phone"
                        value={demoFormData.cellphone}
                        onChange={onFieldChange}
                        placeholder="8135551234"
                        required
                        disabled
                      />
                    </div>
                    <TextInput
                      id="email"
                      label="Email"
                      value={demoFormData.email}
                      onChange={onFieldChange}
                      disabled
                    />
                  </div>

                  <DynamicCheckbox
                    label="Consent to Text"
                    fieldName="consentToText"
                    isChecked={demoFormData.consentToText}
                    onChange={handleCheckChange}
                    isDisabled={true}
                  />

                  <div ref={setTourRef("portal-group")} className={highlightIf("contact")}>
                    <MedicalPortalAccess
                      id="portal"
                      label="Would you like to have access to your Medical Records / Portal?"
                      portalValue={demoFormData.portal}
                      onChange={onFieldChange}
                      isDisabled={true}
                    />
                  </div>

                  <div ref={setTourRef("signature-group")} className={`${highlightIf("signature")} ${requiredHighlightClass}`}>
                    <Signature
                      fieldName="patient_signature"
                      label="Signature of Patient or Guardian of Minor"
                      formData={demoFormData}
                      isEditing={isEditing}
                      isDisabled={true}
                      signatures={signatures}
                      canvasRefs={canvasRefs}
                      invalidFields={invalidFields}
                      updateSignatureData={updateSignatureData}
                      clearSignature={clearSignature}
                      toggleEditMode={toggleEditMode}
                      isNext={true}
                    />
                  </div>
                </div>
              )}

              {formPage === 3 && (
                <div ref={setTourRef("family-group")} className={`space-y-3 ${highlightIf("family")}`}>
                  <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs sm:text-sm text-sky-800">
                    Format guide: `Family DOB` as `MM/DD/YYYY`, phone as valid 10-digit number.
                  </div>
                  <div className="pointer-events-none select-none">
                    <FamilyMemberConsent
                      familyMembers={demoFormData.familyMembers}
                      handleFamilyChange={handleFamilyChange}
                      handleRemoveFamilyMember={handleRemoveFamilyMember}
                      handleAddMember={handleAddMember}
                      fileUpload={false}
                      nextLoader={true}
                      DOBPickerComponent={DOBPickerInput}
                      title="Retina Specialty Institute"
                    />
                  </div>
                </div>
              )}

              <div ref={setTourRef("nav-actions")} className={`flex justify-end gap-2 mt-4 ${highlightIf("nav")}`}>
                <button
                  type="button"
                  onClick={() => setFormPage((p) => Math.max(1, p - 1))}
                  className={buttonClass}
                  disabled={formPage === 1}
                >
                  Previous
                </button>
                {formPage < 3 && (
                  <button
                    type="button"
                    onClick={() => setFormPage((p) => Math.min(3, p + 1))}
                    className={buttonClass}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .help-form-demo textarea,
        .help-form-demo select {
          min-width: 150px !important;
          max-width: 190px !important;
        }
        .help-form-demo div.min-w-\\[200px\\] {
          min-width: 150px !important;
          max-width: 190px !important;
        }
        .tour-spotlight {
          position: fixed;
          border-radius: 12px;
          border: 2px solid #0ea5e9;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.58), 0 0 0 6px rgba(14, 165, 233, 0.2), 0 0 22px rgba(14, 165, 233, 0.85);
          animation: pulseTour 1.5s ease-in-out infinite;
          transition: all 220ms ease;
          pointer-events: none;
          z-index: 70;
        }
        @keyframes pulseTour {
          0% { transform: scale(1); }
          50% { transform: scale(1.01); }
          100% { transform: scale(1); }
        }
        .tour-tooltip {
          position: fixed;
          width: min(360px, calc(100vw - 20px));
          z-index: 80;
          transition: all 220ms ease;
        }
        .tour-arrow {
          position: absolute;
          width: 0;
          height: 0;
          border-style: solid;
        }
        .tour-arrow-top {
          bottom: -8px;
          left: 24px;
          border-width: 8px 8px 0 8px;
          border-color: #ffffff transparent transparent transparent;
        }
        .tour-arrow-bottom {
          top: -8px;
          left: 24px;
          border-width: 0 8px 8px 8px;
          border-color: transparent transparent #ffffff transparent;
        }
        .tour-arrow-left {
          right: -8px;
          top: 24px;
          border-width: 8px 0 8px 8px;
          border-color: transparent transparent transparent #ffffff;
        }
        .tour-arrow-right {
          left: -8px;
          top: 24px;
          border-width: 8px 8px 8px 0;
          border-color: transparent #ffffff transparent transparent;
        }
      `}</style>
      {isTourActive && spotlightRect && (
        <div
          className="tour-spotlight"
          style={{
            top: `${spotlightRect.top - 6}px`,
            left: `${spotlightRect.left - 6}px`,
            width: `${spotlightRect.width + 12}px`,
            height: `${spotlightRect.height + 12}px`,
          }}
        />
      )}
      {isTourActive && currentTourStep && (
        <div
          ref={tooltipRef}
          className="tour-tooltip"
          style={{ top: `${tooltipState.top}px`, left: `${tooltipState.left}px` }}
        >
          <div className="relative bg-white rounded-xl shadow-2xl border border-sky-100 p-4">
            <div
              className={`tour-arrow ${
                tooltipState.placement === "top"
                  ? "tour-arrow-top"
                  : tooltipState.placement === "bottom"
                  ? "tour-arrow-bottom"
                  : tooltipState.placement === "left"
                  ? "tour-arrow-left"
                  : "tour-arrow-right"
              }`}
            />
            <p className="text-xs font-semibold text-sky-700 mb-1">
              Step {tourStepIndex + 1} of {activeTourSteps.length}
            </p>
            <h4 className="text-base font-bold text-sky-700">{currentTourStep.title}</h4>
            <p className="text-sm text-gray-700 mt-1">{currentTourStep.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <button
                type="button"
                onClick={closeTour}
                className="text-sm text-gray-600 hover:text-gray-800 font-semibold"
              >
                Skip
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={goPrevTour}
                  disabled={tourStepIndex === 0}
                  className="px-3 py-1.5 bg-sky-100 text-sky-700 rounded-lg text-sm font-semibold disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={goNextTour}
                  className="px-3 py-1.5 bg-sky-600 text-white rounded-lg text-sm font-semibold hover:bg-sky-700"
                >
                  {tourStepIndex === activeTourSteps.length - 1 ? "Finish" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpForm;
